import { Box, Dialog, Stack, Typography } from '@mui/material';
import { DefaultMeetingSession } from 'amazon-chime-sdk-js';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { EnaraLogo } from '../../../assets/icons';
import {
  ELAPSED_MS_BEFORE_AUTO_MARKING_AS_ATTENDED,
  ELAPSED_MS_BEFORE_START_RECORDING,
  TOAST_AUTO_CLOSE_DELAY_MS,
} from '../../../config/config';
import { useFeatures } from '../../../contexts/features/context';
import { useVideoCall } from '../../../contexts/videoCall/context';
import { isIOS, isMobileDevice } from '../../../helpers/device';
import coreServiceClient from '../../../lib/core/client';
import { VideoCallStatus } from '../../../lib/core/types';
import { logErrorAndReport } from '../../../lib/errorReporting';
import { Features } from '../../../lib/features/client';
import FeatureManager from '../../../lib/features/manager';
import MixpanelClient, { TrackingEvent, TrackingEventV2 } from '../../../lib/tracking/mixpanel';
import videoServiceClient from '../../../lib/video/client';
import { UserType } from '../../../lib/video/types';
import Toast from '../../Toast/Toast';
import { VideoController } from '../VideoController';
import '../chime-video-call.css';
import { Participant } from '../meeting-call.types';
import styles from './../../Meeting/components/JoiningCallModal.module.css';
import Outline from './Outline/Outline';
import RecordingDialog from './RecordingDialog/RecordingDialog';
import { RoomContainer } from './RoomContainer/RoomContainer';
import Toolbox, {
  ClickOptionCallbackParams,
  RefToolbox,
  ToolBoxState,
  ToolboxAction,
} from './Toolbox/Toolbox';
import VideoTrack from './VideoComponents/VideoTrack';
import WaitingForParticipant from './WaitingForParticipant/WaitingForParticipant';

const GESTURE_TIMEOUT_MS = 5000;

type EnaraMeetingProps = {
  loadingMode: boolean;
  onLeaveRoom: (meetingId: string) => void;
};

const EnaraMeeting: FC<EnaraMeetingProps> = ({ loadingMode, onLeaveRoom }) => {
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [meetingSession, setMeetingSession] = useState<DefaultMeetingSession | null>(null);
  const [localParticipant, setLocalParticipant] = useState<Participant | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isSomeoneSharingScreen, setIsSomeoneSharingScreen] = useState(false);

  const didParticipantAttendedOrReschedule = useRef<boolean>(false);
  const autoMarkAsAttendedTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const toolboxRef = useRef<RefToolbox | null>(null);
  const autoMarkAsAttendedAlreadyScheduled = useRef(false);
  const hasRemoteParticipantConnected = useRef(false);

  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioBoundRef = useRef(false);
  const initDoneRef = useRef(false);

  const { featuresState } = useFeatures();
  const {
    videoCallState: { meetingInfo, mediaSettings },
    getParticipantNameBy,
  } = useVideoCall();

  // TODO: read mediaSettings directly from context. This should be made after removing code from old video call implementations
  const [toolboxState, setToolboxState] = useState<ToolBoxState>({
    isAudioOn: mediaSettings.micEnabled,
    isVideoOn: mediaSettings.videoEnabled,
    isSharingScreen: false,
    isRecording:
      meetingInfo?.userType === UserType.Provider && !!meetingInfo.ongoingRecordingPipelineId,
  });

  const participantsObserverHandler = {
    localParticipantUpdated(participant: Participant) {
      if (!localParticipant) {
        if (meetingInfo?.userType !== UserType.Guest) {
          participant.name = getParticipantNameBy(meetingInfo?.userType ?? UserType.Guest);
        }

        setLocalParticipant(participant);
      }
    },
    remoteParticipantsUpdated(remoteParticipants: Participant[]) {
      let sharingScreen = false;
      let participantFound = null;

      for (const participant of remoteParticipants) {
        if (!participantFound && !participant.tileState.boundExternalUserId?.includes('guest')) {
          participantFound = participant;
        }

        if (participant.tileState?.isContent) {
          sharingScreen = true;
        }
      }

      setIsSomeoneSharingScreen(sharingScreen);
      if (participantFound) {
        const name = getParticipantNameBy(
          meetingInfo?.userType === UserType.Provider ? UserType.Member : UserType.Provider
        );
        participantFound.name = name;
      }

      setParticipants([...remoteParticipants]);
      scheduleAutoMarkAsAttendedForProvider(remoteParticipants);

      if (!hasRemoteParticipantConnected.current) {
        hasRemoteParticipantConnected.current = true;

        MixpanelClient.trackEvent({
          eventName: TrackingEventV2.VideoCallRemoteParticipantHasConnected,
          properties: { source: 'unknown' },
        });
      }
    },
  };

  const videoController = useMemo(
    () =>
      new VideoController(
        meetingInfo!.videoConfig?.meeting,
        meetingInfo!.videoConfig?.attendee.Attendee,
        participantsObserverHandler
      ),
    []
  );

  const tryAutoInitialization = async () => {
    if (isMobileDevice() && isIOS()) {
      // When the devices is an iOS device we wait for the user gesture handlers (touch or pointerdown)
      // if not we show the modal to force the user interaction.
      return;
    }

    // Any different device we initialize normally
    await initializeVideoIO();
  };

  const initializeVideoIO = async () => {
    if (initDoneRef.current) {
      return;
    }

    initDoneRef.current = true;
    try {
      const setupResult = await videoController.startSetup({
        micEnabled: toolboxState.isAudioOn,
        videoEnabled: toolboxState.isVideoOn,
      });

      if (!setupResult.success) {
        initDoneRef.current = false;
        throw new Error(setupResult.message || 'Failed to setup video/audio');
      }

      const session = setupResult.session!;
      setMeetingSession(session);

      //Binding only one time the audio
      if (audioRef.current && !audioBoundRef.current) {
        session.audioVideo.bindAudioElement(audioRef.current);
        audioBoundRef.current = true;
      }

      session.audioVideo.start();
      setShowAudioPrompt(false);
    } catch (err) {
      initDoneRef.current = false;
      throw err;
    }
  };

  const onFirstGesture = async () => {
    try {
      await initializeVideoIO();
    } catch (err) {
      logErrorAndReport({ error: err });
      Toast.error('We could not initialize mic/camera. Please check browser and OS permissions.');
    } finally {
      removeAllListeners();
    }
  };

  const addListeners = () => {
    window.addEventListener('pointerdown', onFirstGesture, { once: true });
    window.addEventListener('touchend', onFirstGesture, { once: true });
  };

  const removeAllListeners = () => {
    window.removeEventListener('pointerdown', onFirstGesture);
    window.removeEventListener('touchend', onFirstGesture);
  };

  const enableAudioNow = async () => {
    try {
      await initializeVideoIO();
    } catch (err) {
      logErrorAndReport({ error: err });
      Toast.error('Failed to initialize audio. Please check your browser permissions.');
    }
  };

  useEffect(() => {
    tryAutoInitialization().catch((err) => {
      setShowAudioPrompt(true);
      logErrorAndReport({ error: err });
    });

    addListeners();

    const gestureTimeout = setTimeout(() => {
      if (!initDoneRef.current) {
        setShowAudioPrompt(true);
      }
    }, GESTURE_TIMEOUT_MS);

    return () => {
      clearTimeout(gestureTimeout);
      removeAllListeners();

      (async () => {
        if (videoController && meetingSession) {
          await videoController?.unbindVideoIO();
        }
      })();
    };
  }, []);

  useEffect(() => {
    // Only for providers and recording not started yet
    if (meetingInfo?.userType !== UserType.Provider || toolboxState.isRecording) {
      return;
    }

    // We need both participants to be in the call to start recording
    if (!localParticipant || participants.length === 0) {
      return;
    }

    // If member did not allow this to happen or the feature is not enabled for this env, abort
    if (
      !meetingInfo.isAutomaticRecordingAllowed ||
      !FeatureManager.isAutoRecordingAllowed(featuresState)
    ) {
      return;
    }

    const { provider } = meetingInfo.meetingAttendees;

    Toast.info('The meeting recording will begin in 10 seconds', {
      autoClose: ELAPSED_MS_BEFORE_START_RECORDING,
      onClose: async () => {
        const result = await videoServiceClient.startMeetingCapture(
          meetingInfo.videoConfig?.meeting.MeetingId,
          provider.authToken
        );

        if (result) {
          setToolboxState((prev) => ({ ...prev, isRecording: true }));
          setRecordingDialogOpen(false);
        } else {
          Toast.error('The meeting recording could not be started');
        }
      },
    });
  }, [meetingInfo, toolboxState.isRecording, localParticipant, participants, featuresState]);

  const scheduleAutoMarkAsAttendedForProvider = (participants: Participant[]): void => {
    if (meetingInfo?.userType !== UserType.Provider) {
      return;
    }

    if (
      !didParticipantAttendedOrReschedule.current &&
      participants.length > 0 &&
      !autoMarkAsAttendedAlreadyScheduled.current
    ) {
      autoMarkAsAttendedTimeoutId.current = setTimeout(async () => {
        const result = await updateVideoCallStatus(VideoCallStatus.Attended);

        if (result) {
          didParticipantAttendedOrReschedule.current = true;

          Toast.info('Attendance confirmed', { autoClose: TOAST_AUTO_CLOSE_DELAY_MS });

          MixpanelClient.trackEvent({
            eventName: TrackingEvent.Task,
            properties: { field: 'Auto-mark as Attended', source: 'chime-video' },
          });

          toolboxRef.current?.setMarkedAsAttended();
        }
      }, ELAPSED_MS_BEFORE_AUTO_MARKING_AS_ATTENDED);
      autoMarkAsAttendedAlreadyScheduled.current = true;
    }
  };

  const endCall = () => {
    if (
      meetingInfo?.userType === UserType.Provider &&
      !didParticipantAttendedOrReschedule.current
    ) {
      return Toast.warning(
        `Before ending the call make sure to "Mark as Attended" or "Mark as Reschedule"`,
        {
          position: 'bottom-center',
          className: 'tool-box-toast-center',
        }
      );
    }

    cleanUpAndLeaveRoom();
  };

  const cleanUpAndLeaveRoom = async () => {
    if (autoMarkAsAttendedTimeoutId.current !== null) {
      clearTimeout(autoMarkAsAttendedTimeoutId.current);
    }

    onLeaveRoom(meetingInfo!.videoConfig?.meeting.MeetingId);
    await videoController?.unbindVideoIO();
  };

  const updateVideoCallStatus = async (videoStatus: VideoCallStatus) => {
    if (meetingInfo?.userType !== UserType.Provider) {
      return false;
    }

    const appointmentId = meetingInfo.appointment?.id;
    const { provider } = meetingInfo.meetingAttendees ?? {};

    if (!appointmentId) {
      Toast.error('Could not find an appointment for the member', {
        position: 'bottom-right',
        className: 'tool-box-toast-right',
      });
      return false;
    }
    if (!provider?.id || !provider.authToken) {
      Toast.error('Could not get the provider info', {
        position: 'bottom-right',
        className: 'tool-box-toast-right',
      });
      return false;
    }

    try {
      await coreServiceClient.setVideoCallStatusTo({
        scheduleId: appointmentId,
        providerId: provider.id,
        providerAuthToken: provider.authToken,
        videoCallStatus: videoStatus,
      });
    } catch (error) {
      Toast.error('Could not update video call status', {
        position: 'bottom-right',
        className: 'tool-box-toast-right',
      });
      logErrorAndReport({ error });
      return false;
    }
    return true;
  };

  const handleVideoCallStatusUpdate = async (status: VideoCallStatus) => {
    didParticipantAttendedOrReschedule.current = true;

    const result = await updateVideoCallStatus(status);

    if (result) {
      Toast.info(
        status === VideoCallStatus.Rescheduled ? 'Marked as Reschedule' : 'Marked as Attended',
        {
          position: 'bottom-right',
          className: 'tool-box-toast-right',
        }
      );
    }

    return result;
  };

  const handleClickToolboxOption = async (params: ClickOptionCallbackParams) => {
    if (!meetingSession) {
      return false;
    }

    const { action } = params;

    switch (action) {
      case ToolboxAction.StartRecording:
        setRecordingDialogOpen(!recordingDialogOpen);
        MixpanelClient.trackEvent({
          eventName: TrackingEventV2.VideoCallStartRecording,
          properties: { field: 'Record Meeting' },
        });
        return true;

      case ToolboxAction.ScreenSharing:
        if (toolboxState.isSharingScreen) {
          meetingSession.audioVideo.stopContentShare();
        } else {
          await meetingSession.audioVideo.startContentShareFromScreenCapture();
        }

        setToolboxState((previousState) => {
          return {
            ...previousState,
            isSharingScreen: !previousState.isSharingScreen,
          };
        });
        return true;

      case ToolboxAction.ToggleTrackVideo:
        if (toolboxState.isVideoOn) {
          await videoController?.stopVideo();
        } else {
          await videoController?.startVideo();
        }

        setToolboxState((previousState) => {
          return {
            ...previousState,
            isVideoOn: !previousState.isVideoOn,
          };
        });
        return true;

      case ToolboxAction.ToggleTrackAudio:
        if (toolboxState.isAudioOn) {
          meetingSession.audioVideo.realtimeMuteLocalAudio();
        } else {
          meetingSession.audioVideo.realtimeUnmuteLocalAudio();
        }

        setToolboxState((previousState) => {
          return {
            ...previousState,
            isAudioOn: !previousState.isAudioOn,
          };
        });
        return true;

      case ToolboxAction.LeaveRoom:
        endCall();
        return true;

      case ToolboxAction.VideoCallStatus:
        return handleVideoCallStatusUpdate(params.status);

      case ToolboxAction.ShowOutline:
        setOutlineOpen(true);
        MixpanelClient.trackEvent({
          eventName: TrackingEventV2.VideoCallOutlineOpen,
          properties: { field: 'Show Outline' },
        });
        return true;

      default:
        logErrorAndReport({
          error: `[handleClickToolboxOption] Unknown action: ${action}`,
        });
    }

    return false;
  };

  const handleOnRecordingConfirm = async () => {
    setToolboxState((previousState) => ({ ...previousState, isRecording: true }));
  };

  const handleOutlineClose = () => {
    setOutlineOpen(false);
    MixpanelClient.trackEvent({
      eventName: TrackingEventV2.VideoCallOutlineClose,
      properties: { field: 'Close Outline' },
    });
  };

  const renderShareScreenView = () => {
    return (
      <>
        {/* Screen share container */}
        <Stack className='chime-video-call-container__screen-share'>
          {participants.map(
            (tileParticipant, index) =>
              tileParticipant?.tileState?.isContent && (
                <VideoTrack
                  participant={tileParticipant}
                  session={meetingSession}
                  key={`${index}-${tileParticipant.tileState.boundExternalUserId}`}
                  className='chime-video-call-container__video-track--content'
                  isScreenShare={true}
                />
              )
          )}
        </Stack>
        {/* Participant videos */}
        <Stack
          sx={{ flexDirection: 'row' }}
          className='chime-video-call-container__participant-videos'>
          {participants.map(
            (tileParticipant, index) =>
              !tileParticipant?.tileState?.isContent && (
                <VideoTrack
                  participant={tileParticipant}
                  session={meetingSession}
                  key={`${tileParticipant.tileState.boundExternalUserId}`}
                  className='chime-video-call-container__video-track--remote'
                />
              )
          )}
          {localParticipant && (
            <VideoTrack
              participant={localParticipant}
              session={meetingSession}
              className='chime-video-call-container__video-track--local'
            />
          )}
        </Stack>
      </>
    );
  };

  const renderOneOnOneView = () => {
    return (
      <>
        <Stack className='chime-video-call-container__participants'>
          {participants.length === 0 ? (
            <Stack className='chime-video-call-container__waiting'>
              <WaitingForParticipant
                numberOfRemoteParticipants={participants.length}
                onMarkCallAsNoShow={cleanUpAndLeaveRoom}
              />
            </Stack>
          ) : (
            <Stack className='chime-video-call-container__remote-video'>
              {participants.map((tileParticipant, index) => (
                <VideoTrack
                  participant={tileParticipant}
                  session={meetingSession}
                  key={`${tileParticipant.tileState.boundExternalUserId}`}
                  className='chime-video-call-container__video-track--remote'
                />
              ))}
            </Stack>
          )}

          <Stack className='chime-video-call-container__local-videos'>
            {localParticipant && (
              <VideoTrack
                participant={localParticipant}
                session={meetingSession}
                className='chime-video-call-container__video-track--local'
              />
            )}
          </Stack>
        </Stack>
      </>
    );
  };

  return (
    <Box
      className={`chime-video-call-container ${
        isSomeoneSharingScreen ? 'app-chime--screen-share' : ''
      }`}>
      <RoomContainer open={outlineOpen}>
        {isSomeoneSharingScreen ? renderShareScreenView() : renderOneOnOneView()}
      </RoomContainer>

      {meetingInfo?.userType === UserType.Provider &&
        meetingInfo.providerConfig?.scheduling.viewRecording && (
          <RecordingDialog
            isRecording={toolboxState.isRecording}
            isOpen={recordingDialogOpen}
            onConfirm={handleOnRecordingConfirm}
            onClose={() => setRecordingDialogOpen(false)}
          />
        )}

      {FeatureManager.shouldRenderOutlineForAppointment({
        feature: featuresState[Features.MembersWebAppShowOutline],
        userType: meetingInfo?.userType ?? UserType.Guest,
        appointmentType:
          meetingInfo && 'appointment' in meetingInfo ? meetingInfo.appointment?.type : undefined,
      }) && <Outline isOpen={outlineOpen} onClose={handleOutlineClose} />}

      <Toolbox
        ref={toolboxRef}
        toolboxState={toolboxState}
        loadingMode={loadingMode}
        onClickOption={handleClickToolboxOption}
      />

      <audio ref={audioRef} id='meeting-audio' autoPlay playsInline webkit-playsinline='' />
      {showAudioPrompt && !initDoneRef.current && (
        <Dialog
          open={showAudioPrompt}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            className: styles.modalPaper,
          }}>
          <Box className={styles.modalContent}>
            <img src={EnaraLogo} alt='Enara Logo' className={styles.logo} />
            <Typography variant='h6' className={styles.text}>
              {`We were unable to initialize the audio device for your call. Please tap the button below.`}
            </Typography>
            <button onClick={enableAudioNow}>Enable audio</button>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default EnaraMeeting;
